import type { ProjectListItem } from '@shared/types/project';
import ProjectCard from './ProjectCard';
import InlineEditableName from '~/components/ui/InlineEditableName';

interface ProjectListProps {
  projects: ProjectListItem[];
  onDelete: (id: string) => void;
  onClick: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onNew: () => void;
  view: 'grid' | 'list';
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  if (days < 30) {
    return `${days}d ago`;
  }

  return new Date(dateStr).toLocaleDateString();
}

const LIST_COLS = 'grid-cols-[minmax(0,1fr)_auto_40px] sm:grid-cols-[2fr_2.5fr_1fr_1fr_40px]';

const SWATCH_PALETTE = ['#1A2330', '#E8DFD3', '#E5E2D8', '#15151A', '#FAF6E8', '#0F1419', '#3A2418', '#0E0E12'];

function swatch(seed: string): string {
  let hash = 0;

  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }

  return SWATCH_PALETTE[Math.abs(hash) % SWATCH_PALETTE.length];
}

export default function ProjectList({ projects, onDelete, onClick, onRename, onNew, view }: ProjectListProps) {
  if (view === 'list') {
    return (
      <div className="border border-hairline rounded-xl overflow-hidden bg-surface">
        {/* Phones keep name · updated · delete; description and chats need the room. */}
        <div className={`grid gap-3.5 px-4.5 py-2.5 bg-raised ${LIST_COLS}`}>
          {['name', 'description', 'chats', 'updated', ''].map((h) => (
            <div key={h} className={`ui-caption ${h === 'description' || h === 'chats' ? 'hidden sm:block' : ''}`}>
              {h}
            </div>
          ))}
        </div>
        {projects.map((p) => (
          <div
            key={p.id}
            onClick={() => onClick(p.id)}
            className={`group grid gap-3.5 px-4.5 py-3 border-t border-hairline items-center text-[12.5px] cursor-pointer hover:bg-white/[0.02] transition-colors ${LIST_COLS}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="w-7 h-7 rounded-md flex-shrink-0 border border-subtle"
                style={{ background: swatch(p.name) }}
              />
              <InlineEditableName
                value={p.name}
                onSave={(name) => onRename(p.id, name)}
                className="font-geist-mono text-primary min-w-0"
                inputClassName="font-geist-mono text-primary bg-sunken border border-accent rounded px-1.5 py-0.5 outline-none w-full"
              />
            </div>
            <span className="hidden sm:block text-secondary truncate">{p.description || '—'}</span>
            <span className="hidden sm:block font-geist-mono text-muted text-[11px] truncate">
              {p.chatCount} {p.chatCount === 1 ? 'chat' : 'chats'}
            </span>
            <span className="text-muted text-[11px]">{timeAgo(p.lastAccessedAt)}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(p.id);
              }}
              className="ui-btn-icon justify-self-end hover:text-danger"
              title="Delete project"
            >
              <span className="i-ph:trash text-sm" />
            </button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
      {projects.map((project) => (
        <ProjectCard key={project.id} project={project} onDelete={onDelete} onClick={onClick} onRename={onRename} />
      ))}
      <button
        onClick={onNew}
        className="bg-surface border border-dashed border-subtle rounded-xl flex flex-col items-center justify-center gap-2.5 text-muted text-[12.5px] min-h-[200px] p-6 cursor-pointer hover:border-prominent hover:text-secondary transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-accent/[0.08] border border-accent/25 text-accent flex items-center justify-center">
          <span className="i-ph:plus text-sm" />
        </div>
        <span>New project</span>
        <span className="text-[10.5px] text-faint">or paste a brief</span>
      </button>
    </div>
  );
}
